import { Button } from "@porchpass/ui";
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "src/api/firebase/connections";
import { Logo } from "src/components/Logo";
import { Footer } from "../LoginPage/Footer";
import { PasswordRequirementsText } from "./PasswordRequirementsText";
import { ResetPasswordForm } from "./ResetPasswordForm";

type ActionMode = "resetPassword" | "recoverEmail" | "verifyEmail" | "signIn";

const AuthActionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get("mode") as ActionMode | null;
  const actionCode = searchParams.get("oobCode");
  const invited = searchParams.get("invited") === "true";
  const continueUrl = searchParams.get("continueUrl");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");

  useEffect(() => {
    if (!mode || !actionCode) {
      setError("Invalid or missing action parameters");
      setLoading(false);
      return;
    }

    // Handle magic link sign-in
    if (mode === "signIn") {
      const currentUrl = window.location.href;

      if (isSignInWithEmailLink(auth, currentUrl)) {
        // For cross-domain magic links, we need to redirect back to the
        // originating app with the magic link parameters intact so that
        // the sign-in happens on the correct domain (Firebase auth state
        // is per-origin).
        if (continueUrl) {
          try {
            const continueUrlObj = new URL(continueUrl);
            // Check if this is a cross-domain redirect (different origin)
            const currentOrigin = window.location.origin;
            const continueOrigin = continueUrlObj.origin;

            if (currentOrigin !== continueOrigin) {
              // Redirect back to the originating app with the full magic link URL
              // so they can complete the sign-in on their domain
              const redirectUrl = new URL(continueUrl);
              redirectUrl.searchParams.set("magicLink", currentUrl);
              window.location.href = redirectUrl.toString();
              return;
            }
          } catch (e) {
            console.error("Failed to parse continueUrl:", e);
          }
        }

        // Same-origin sign-in flow (or no continueUrl)
        // Try to get email from multiple sources:
        // 1. Email parameter in the action URL directly
        // 2. Email parameter extracted from continueUrl
        // 3. localStorage (for same-device flow)
        // 4. Prompt user (fallback for different device)
        let userEmail = searchParams.get("email");

        // If not found directly, try to extract from continueUrl
        if (!userEmail && continueUrl) {
          try {
            const continueUrlObj = new URL(continueUrl);
            userEmail = continueUrlObj.searchParams.get("email");
          } catch (e) {
            console.error("Failed to parse continueUrl:", e);
          }
        }

        if (!userEmail) {
          userEmail = window.localStorage.getItem("emailForSignIn");
        }

        if (!userEmail) {
          // User opened the link on a different device without email parameter
          userEmail = window.prompt(
            "Please provide your email for confirmation",
          );
        }

        if (!userEmail) {
          setError("Email is required to complete sign-in");
          setLoading(false);
          return;
        }

        // Sign in with the email link
        signInWithEmailLink(auth, userEmail, currentUrl)
          .then(() => {
            // Clear the email from localStorage
            window.localStorage.removeItem("emailForSignIn");

            // Redirect to continue URL or default dashboard
            const redirectUrl = continueUrl || "/finding-user-dealer/dashboard";
            window.location.href = redirectUrl;
          })
          .catch((err: { code?: string }) => {
            console.error("Error signing in with email link:", err);
            if (err.code === "auth/invalid-action-code") {
              setError("This link has expired. Please request a new one.");
            } else if (err.code === "auth/invalid-email") {
              setError("The email address is invalid. Please try again.");
            } else {
              setError("Failed to sign in. Please request a new link.");
            }
            setLoading(false);
          });
      } else {
        setError("This is not a valid sign-in link.");
        setLoading(false);
      }
      return;
    }

    // Verify the action code for password reset
    verifyPasswordResetCode(auth, actionCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error verifying action code:", err);
        setError(
          "This link is invalid or has expired. Please request a new one.",
        );
        setLoading(false);
      });
  }, [mode, actionCode, continueUrl]);

  const handleResetPassword = (newPassword: string) => {
    if (!actionCode) return;

    setLoading(true);
    confirmPasswordReset(auth, actionCode, newPassword)
      .then(() => {
        setSuccess(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error resetting password:", err);
        setError("Failed to reset password. The link may have expired.");
        setLoading(false);
      });
  };

  if (loading && !success) {
    return (
      <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
        <div className="flex flex-col items-center w-full">
          <div className="mt-8">
            <Logo />
          </div>
          <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px] text-center">
            <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary mb-4">
              Verifying...
            </h1>
            <p className="text-[13px] font-normal leading-[17px] text-on-surface-muted">
              Please wait while we verify your request.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
        <div className="flex flex-col items-center w-full">
          <div className="mt-8">
            <Logo />
          </div>
          <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px] text-center">
            <div className="flex flex-col gap-4 items-center mb-10">
              <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary">
                Invalid Link
              </h1>
              <p className="text-[13px] font-normal leading-[17px] text-on-surface-muted">
                {error}
              </p>
            </div>
            <a href="/login/forgot-password" className="w-full">
              <Button variant="ghost" size="m" className="w-full">
                Request a new password reset link
              </Button>
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
        <div className="flex flex-col items-center w-full">
          <div className="mt-8">
            <Logo />
          </div>
          <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px] text-center">
            <div className="flex flex-col gap-4 items-center mb-10">
              <div className="flex gap-1 items-center">
                <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary">
                  Password reset
                </h1>
                <div className="bg-[#dbf8e7] rounded-[24px] px-1.5 py-1 flex items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      opacity="0.4"
                      d="M11.6715 0.135754C10.6643 4.39882e-06 9.37351 0 7.73026 0H7.64475C6.0015 0 4.71074 4.39882e-06 3.70349 0.135754C2.67074 0.274504 1.85176 0.56474 1.20826 1.20824C0.564758 1.85174 0.274504 2.67074 0.135754 3.70349C4.39882e-06 4.71074 0 6.00151 0 7.64476V7.73024C0 9.37349 4.39882e-06 10.6643 0.135754 11.6715C0.274504 12.7043 0.564758 13.5233 1.20826 14.1668C1.85176 14.8103 2.67074 15.1005 3.70349 15.2392C4.71074 15.375 6.0015 15.375 7.64475 15.375H7.73026C9.37351 15.375 10.6643 15.375 11.6715 15.2392C12.7043 15.1005 13.5232 14.8103 14.1667 14.1668C14.8102 13.5233 15.1005 12.7043 15.2392 11.6715C15.375 10.6643 15.375 9.37349 15.375 7.73024V7.64476C15.375 6.00151 15.375 4.71074 15.2392 3.70349C15.1005 2.67074 14.8102 1.85174 14.1667 1.20824C13.5232 0.56474 12.7043 0.274504 11.6715 0.135754Z"
                      fill="#017C3F"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.9394 0.755168C15.2477 1.03192 15.2724 1.50593 14.9957 1.81418L8.24494 9.31418C8.10769 9.46718 7.91344 9.55643 7.70719 9.56243C7.50169 9.56768 7.30294 9.48817 7.15744 9.34267L4.53244 6.71767C4.23919 6.42517 4.23919 5.94969 4.53244 5.65719C4.82494 5.36394 5.30045 5.36394 5.59295 5.65719L7.6592 7.72343L13.8804 0.810685C14.1572 0.503185 14.6319 0.477668 14.9394 0.755168Z"
                      fill="#017C3F"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-[13px] font-normal leading-[17px] text-on-surface-muted">
                You can now sign in with your new password.
              </p>
            </div>
            <Button
              variant="primary"
              size="m"
              onClick={() => {
                void navigate("/login");
              }}
              className="w-full"
            >
              Continue
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (mode === "resetPassword" && email) {
    return (
      <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
        <div className="flex flex-col items-center w-full">
          <div className="mt-8">
            <Logo />
          </div>
          <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px]">
            <div className="flex flex-col gap-6 items-center mb-10 text-center">
              <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary w-[394px]">
                {invited
                  ? "To begin, set your password"
                  : "Reset your password"}
              </h1>
              <PasswordRequirementsText password={currentPassword} />
            </div>
            <ResetPasswordForm
              onSubmit={handleResetPassword}
              onPasswordChange={setCurrentPassword}
              loading={loading}
              invited={invited}
            />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-between items-center min-h-screen bg-surface-muted">
      <div className="flex flex-col items-center w-full">
        <div className="mt-8">
          <Logo />
        </div>
        <div className="flex flex-col items-center mx-auto mt-[200px] w-[410px] text-center">
          <h1 className="text-[28px] font-semibold leading-[32px] text-on-surface-primary mb-4">
            Invalid Request
          </h1>
          <p className="text-[13px] font-normal leading-[17px] text-on-surface-muted">
            This action is not supported.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AuthActionPage;
